import onDocumentLoaded from "../../lib/onDocumentLoaded";
import AutocompleteHelper, {AutocompleteItem, AutocompleteSettings} from "./AutocompleterHelper";
import * as $ from "jquery";
import {Analytics} from '@plaudit/analytics';
const analytics = Analytics.getInstance();

interface PlauditAutocompleteResponse {
	suggester: string;
	results: PlauditAutocompleteOption[];
}

interface CountyMappings {
	[key: string]: Array<[string, string]>;
}

interface PlauditAutocompleteOption extends AutocompleteItem {
	href?: string;
	fields?: object;
}

export default class Autocompleter {
	static setup() {
		onDocumentLoaded(document, Autocompleter.onReady);
	}

	// This is called when everything is ready.
	private static onReady = () => {
		const geocoder = new google.maps.Geocoder();
		
		// Get base URI (note this is not actually the base URI for the page. It is the root.
		let baseURI = window.location.origin;
		if (baseURI.charAt(baseURI.length - 1) !== '/') {
			baseURI = baseURI + '/';
		}

		const cleanUpCountyName = (countyName: string) => countyName.replace(/(\([^)]+\)|County)\s*/gi, "").trim();

		let retrievedCountyMappings: CountyMappings = null;
		const loadCountyMappings = (data: any) => {
			const countyMappings: CountyMappings = {};
			for (const key in data) {
				if (data.hasOwnProperty(key)) {
					const withoutParens = cleanUpCountyName(key);
					const currentCountyMapping = countyMappings[withoutParens];
					if (currentCountyMapping === null || currentCountyMapping === undefined) {
						countyMappings[withoutParens] = [[cleanUpCountyName(key), data[key].replace(/\\\//gi, '/')]];
					} else {
						currentCountyMapping.push([cleanUpCountyName(key), data[key].replace(/\\\//gi, '/')]);
					}
				}
			}
			return countyMappings;
		}

		$.ajax({
			dataType: "json",
			url: baseURI + 'wp-json/plaudit-site/v1/county-mappings',
			success: data => {
				retrievedCountyMappings = loadCountyMappings(data);
			},
			async: true
		});

		const performWithCountyMappings = (after: (countyMappings: CountyMappings) => void) => {
			if (retrievedCountyMappings !== null) {
				return after(retrievedCountyMappings);
			}
			$.ajax({
				dataType: "json",
				url: baseURI + 'wp-json/plaudit-site/v1/county-mappings',
				success: data => {
					after(retrievedCountyMappings = loadCountyMappings(data));
				},
				async: false
			});
		};

		// Grab all autocomplete input fields on a page
		const suggestionBoxes = document.body.querySelectorAll("input:not(.autocompleter--isSetup).county-autocomplete-input");

		for (let i = 0; i < suggestionBoxes.length; i++) {
			const input = suggestionBoxes.item(i) as HTMLInputElement;
			let onSelectedValidOption: boolean = false;

			const settings: AutocompleteSettings<PlauditAutocompleteOption> = {
				className: 'county-autocomplete',
				input: input,
				validateValue: (value: string): boolean => {
					// If only numbers (zip code) then require 5 digts, otherwise
					// we require a minimum of 4 digits
					if (/^\d+$/.test(value)) {
						return value.length >= 5;
					}
					return value.length >= 4;
				},
				showOnFocus: true,
				hideOnBlur: false,
				fetch: (text, update) => {
					const delegatedUpdate = (items: false | PlauditAutocompleteOption[]) => {
						update(items);
					}
					
					if (text.match(/^[0-9]{5}$/)) {
						const flattenedResults: PlauditAutocompleteOption[] = [];
						performWithCountyMappings(countyMappings => {
							$.ajax({
								dataType: "json",
								url: baseURI + 'wp-json/plaudit-site/v1/zip-to-county/' + text,
								success: data => {
									(data.counties as string).split('|')
										.map(cm => countyMappings[cm])
										.reduce((acc, x) => acc.concat(x), [])
										.map(cm => {return {label: cm[0], href: cm[1]} as PlauditAutocompleteOption;})
										.forEach(res => flattenedResults.push(res));
								},
								async: false
							});
						});
						if (flattenedResults.length !== 0) {
							flattenedResults.sort((a, b) => a.label.localeCompare(b.label));
							delegatedUpdate(flattenedResults);
							return;
						}
					}
					geocoder.geocode( { 'address': text}, (results, status) => {
						if (status == 'OK') {
							performWithCountyMappings(countyMappings => {
								const encounteredURLs: string[] = []; // Data storage for the uniqueness filter

								let flattenedResults = results
									.map(result => {
										let stateSuffix = '-';
										const stateComponents = result.address_components.filter(ac => ac.types?.indexOf("administrative_area_level_1") !== -1);
										if (stateComponents.length > 0) {
											stateSuffix = stateSuffix + stateComponents[0].short_name;
										}
										const resolvedCountyMappings = result.address_components
											.filter(ac => ac.types?.indexOf("administrative_area_level_2") !== -1)
											.map(ac => cleanUpCountyName(ac.long_name) + stateSuffix)
											.map(cn => countyMappings[cn] ?? [])
											.reduce((acc, x) => acc.concat(x), []);
										result.postcode_localities?.map(cn => cleanUpCountyName(cn) + stateSuffix)
											.map(cn => countyMappings[cn] ?? [])
											.reduce((acc, x) => acc.concat(x), [])
											.forEach(itm => resolvedCountyMappings.push(itm));
										return resolvedCountyMappings;
									})
									.reduce((acc, x) => acc.concat(x), [])
									.filter(displayableURL => {
										if (encounteredURLs.indexOf(displayableURL[1]) === -1) {
											encounteredURLs.push(displayableURL[1]);
											return true;
										}
										return false;
									})
									.map(displayableURL => {return {label: displayableURL[0], href: displayableURL[1]} as PlauditAutocompleteOption;});

								Object.keys(countyMappings)
									.filter(cm => countyMappings.hasOwnProperty(cm))
									.filter(cm => (cm as string).substring(0, text.length).toLowerCase() === text.toLowerCase())
									.map(cm => countyMappings[cm])
									.reduce((acc, x) => acc.concat(x), [])
									.map(cm => {return {label: cm[0], href: cm[1]} as PlauditAutocompleteOption;})
									.filter(cm => flattenedResults.filter(ecm => ecm.label === cm.label).length === 0)
									.forEach(res => flattenedResults.push(res));

								flattenedResults.sort((a, b) => a.label.localeCompare(b.label));
								delegatedUpdate(flattenedResults);
							});
						} else {
							delegatedUpdate([]);
						}
					});
				},
				onFetchStart: () => {
					document.querySelector(".locationSearch .searchForm__btn")?.classList.add("btn--isWaiting");
				},
				onFetchEnd: () => {
					document.querySelector('.locationSearch .searchForm__btn')?.classList.remove("btn--isWaiting");
				},
				onSelect: item => {
					onSelectedValidOption = item.href !== undefined;
					if (!onSelectedValidOption) {
						// Not a valid option
						return;
					}

					// Track use of auto-complete
					let label = item.label.replace(/<\/?span( [^>]*)?>/g, '');

					analytics.trackEvent("Search:county", "Autocomplete Option Selected", [input.value, label, item.href]);

					// Change page to what was selected
					let href = item.href;
					if (!new RegExp('^(?:[a-z]+:)?//', 'i').test(href)) {
						const bases = document.getElementsByTagName('base');
						if (!bases || bases.length < 1) {
							href = item.href
						} else {
							const base: HTMLBaseElement = bases[0];
							href = base.href;
							if (href.charAt(href.length - 1) !== '/') {
								href = href + '/';
							}
							href = href + item.href;
						}
					}
					location.href = href;
				},
				render: (item, currentValue) => {
					// Add data to the display
					const div = document.createElement("div");
					div.className = 'autocomplete__option suggestion';

					const labelWrapper = document.createElement("div");
					div.appendChild(labelWrapper);
					labelWrapper.innerHTML += '<span class="autocomplete__optionLabel">' + '<span class="autocomplete__optionLabelHeader">Local branch serving</span> ' + item.label.substring(0, item.label.lastIndexOf('-')) + ' County, ' + item.label.substring(item.label.lastIndexOf('-') + 1) + '</span><a href="' + item.href + '" class="autocomplete__detailsLink"><span class="autocomplete__detailsLinkLabel">View</span</a>'; // Note that 'label' is html

					return div;
				},
				debounceWait: 250,
				emptyMsg: 'No location found. Please try a 5-digit zip code or click a state below.'
			};

			const autocompleteHelper = new AutocompleteHelper<PlauditAutocompleteOption>(settings);

			// TODO Move the next two things into AutocompleterHelper
			input.addEventListener('keypress', evt => {
				// This prevents submission of the form when results are present.
				// Their implementation always prevents form submission regardless of the presence of results.
				if (onSelectedValidOption && evt.key === 'Enter' && document.body.querySelector('.autocomplete .empty') === null) {
					evt.preventDefault();
				}
			});

			const searchButton = document.querySelector('.locationSearch .searchForm__btn');
			if (searchButton !== null) {
				searchButton.addEventListener("click", () => {
					autocompleteHelper.hide();
					autocompleteHelper.fetch(200); // Short delay so the user "feels" it.
				});
			}

			document.body.addEventListener("click", (event: MouseEvent) => {
				const containersToIgnore = document.querySelectorAll(".locationSearch, .autocomplete ");
				const elementClicked = event.target;
				let shouldClose = true;

				if (elementClicked instanceof Element) {
					for (let i=0; i < containersToIgnore.length; i++) {
						const container = containersToIgnore[i];
						if (elementClicked === container || container.contains(elementClicked)) {
							shouldClose = false;
							break;
						}
					}
				}

				if (shouldClose) {
					autocompleteHelper.hide();
				}
			});

			// Mark that the setup is completed
			input.classList.add("autocompleter--isSetup");
		}
	}
}



